/**
 * The one primary action of a case, and the one status word of a line.
 *
 * The minimal case page (12 Oct 2026) shows one big button whose label names the next
 * step: Confirm the customer, Pick 2 items, Check, Prepare the reply, Copy the reply,
 * Mark done and next, Nothing to do. `primaryActionOf` evaluates the states in the order
 * sales work through a case; the first match wins. Everything else sits under "More".
 *
 * `lineWordOf` maps `lineOutcome` to the four words of the draft list: matched, confirm,
 * pick, no item. A removed request and a stand-in line (the whole mail) get no word: they
 * are hidden and counted in the draft caption.
 *
 * The ease round (13 Oct 2026) adds what stands between the case and a reply: the server
 * sends `blockers` on the case, and `captionOf` turns the blocker of the action into the
 * one sentence next to the button, with the control it points at. `primaryActionOf` keeps
 * the order and the kinds; it reads the rules of the case only, never the quotation
 * preview for the order system (its checks belong to another step).
 *
 * Pure: no React, no requests. The components pass what they read (the newest draft,
 * whether the reply was copied in this visit).
 */

import {
  replyWaitingText,
  type StripCustomer,
  type StripLines,
} from "@/features/case-review/case-summary-strip";
import { lineOutcome, replyEligible } from "@/features/case-review/line-semantics";
import { type CaseBlocker, type CaseQuotation, personName, type RuleReason } from "@/lib/api";
import { caseStatusOf, heldStatusOf } from "@/lib/case-status";
import { isOutOfScopeCase } from "@/lib/intents";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import {
  ACTION_LABELS,
  BLOCKER_OPEN_NOTES,
  BLOCKER_WORDS,
  confirmsInOneClick,
  customerConfirmedText,
  heldRuleWord,
  LINE_STATUS_WORDS,
  type LineStatusWord,
  LONG_LIST_WORDS,
  type NoLinesFacts,
  READING_AGAIN_TEXT,
  SHADOW_SENTENCE,
  suggestedCustomerText,
} from "@/lib/sales-flow";
import {
  attachmentFileText,
  attachmentNoteLabel,
  closedBySentence,
  formatDay,
  formatDayTime,
} from "@/lib/work-labels";

/** The word of a line in the draft list; null for a row that is hidden. */
export function lineWordOf(line: QuoteLine): LineStatusWord | null {
  const outcome = lineOutcome(line);
  switch (outcome.kind) {
    case "removed":
    case "not_a_line":
      return null;
    case "matched":
    case "decided":
      return LINE_STATUS_WORDS.matched;
    case "suggested":
      // A unit check on the line does not change the word: the item is still to confirm.
      return LINE_STATUS_WORDS.confirm;
    case "open":
    case "failed":
      return LINE_STATUS_WORDS.pick;
    case "no_item":
      return LINE_STATUS_WORDS.noItem;
  }
}

/**
 * True for a line the bulk actions skip (hunt round 2): its item does not fit the row
 * (`mismatch`), or one reading of a scan gave it (`readOnce`). A person checks it alone.
 */
export function needsOneByOne(line: Pick<QuoteLine, "mismatch" | "readOnce">): boolean {
  return Boolean(line.mismatch) || line.readOnce === true;
}

/** The lines of the draft list, with their word; hidden rows left out. */
export function wordedLines(
  lines: readonly QuoteLine[],
): { line: QuoteLine; word: LineStatusWord }[] {
  return lines.flatMap((line) => {
    const word = lineWordOf(line);
    return word ? [{ line, word }] : [];
  });
}

/** The lines a person still has to confirm or pick. */
export function openLines(lines: readonly QuoteLine[]): QuoteLine[] {
  return wordedLines(lines)
    .filter(({ word }) => word === LINE_STATUS_WORDS.confirm || word === LINE_STATUS_WORDS.pick)
    .map(({ line }) => line);
}

/** What "More" offers on top of the always-present items, by state. */
export type ExtraAction =
  | "try_again"
  | "reopen_as_inquiry"
  | "reopen"
  | "mark_done"
  | "next_case"
  | "not_for_us"
  | "not_them"
  | "pick_one_by_one"
  | "no_item"
  | "add_line"
  | "approve_price"
  | "waiting_customer"
  | "copy_anyway"
  | "edit_text"
  | "undo_edits"
  | "done_and_next"
  | "copy_again";

export type PrimaryKind =
  | "reading"
  | "set_status"
  | "nothing"
  | "take_over"
  | "find_customer"
  | "confirm_customer"
  | "choose_customer"
  | "accept"
  | "confirm_lines"
  | "pick"
  | "approve_price"
  | "check"
  | "prepare"
  | "prepare_again"
  | "copy"
  | "done_and_next";

export interface PrimaryAction {
  kind: PrimaryKind;
  /** The button text (a Badge text for `reading`). */
  label: string;
  /** The caption next to the button while the case sends no blockers. */
  caption: string;
  /**
   * The line the button points at: the first to confirm or pick (`pick`,
   * `confirm_lines`), the held line (`approve_price`, and `check` when the rule names one).
   */
  lineId?: string;
  /** The keys of the suggested lines the button confirms in one write (`confirm_lines`). */
  lineKeys?: string[];
  /** The index in `detail.lines` of the held line the button approves (`approve_price`). */
  lineIndex?: number;
  /** The rule that holds the case (`check`, `approve_price`). */
  ruleId?: string;
  /** The suggested account the button confirms (`confirm_customer`). */
  account?: string;
  /** A case the caption links to (the same inquiry, the first copy). */
  captionCase?: string | null;
  /** The extra secondary actions of this state. */
  extras: ExtraAction[];
}

/** What the components read beside the case. */
export interface ActionFacts {
  /** The viewer's address, to tell "mine" from a colleague's. */
  me: string | null;
  /** A reply draft with text exists. */
  hasDraft: boolean;
  /** The draft was made before the case changed. */
  draftStale: boolean;
  /** When the reply was copied in this visit (ISO), or null. */
  copiedAt: string | null;
  /** True when the viewer may approve prices. */
  mayApprove?: boolean;
}

function sameUser(a: string | null | undefined, b: string | null | undefined): boolean {
  return Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());
}

/**
 * The caption of a case closed for no activity (decision D4, 11 Oct 2026): the server's
 * sentence, the same one the Closed tab shows, and how to undo it.
 */
export function noActivityCaption(sentence: string | null | undefined): string {
  const said = (sentence ?? "").trim().replace(/[.\s]+$/, "");
  return `${said || "No activity for 21 days, closed in I2Q only"}. Reopen it if it is still open.`;
}

/** "Done by Priya Shah on 07 Oct 2026", or the closing sentence of the server. */
function closedCaption(detail: QuoteCaseDetail): string {
  const status = caseStatusOf(detail);
  const work = detail.work ?? null;
  if (detail.workStatus === "replied" || detail.workStatus === "auto_closed") {
    return closedBySentence(detail.closedBy) ?? status.label;
  }
  if (detail.workStatus === "no_activity") {
    return noActivityCaption(detail.closeCandidate?.sentence);
  }
  const by = work?.status_by ? ` by ${personName(work.status_by)}` : "";
  const on = work?.status_at ? ` on ${formatDay(work.status_at)}` : "";
  return `${status.label}${by}${on}`;
}

/**
 * The sources that mean the quotation answers this mail: sales sent the document, or F&O
 * holds a quotation for this sender that answers the inquiry. A number the customer
 * merely wrote in the subject or the text, a PO reference or a quotation sent back with
 * a question is context for the Related section, not the answer to the mail: a repeat
 * request "as on your quotation SQ..." still needs its own quote.
 */
const ANSWERING_SOURCES: ReadonlySet<CaseQuotation["source"]> = new Set([
  "sent_document",
  "fo_contact",
]);

/** The quotation in F&O that already answers the case (sent or confirmed), or null. */
export function quotedAs(detail: QuoteCaseDetail): string | null {
  const sent = (detail.quotations ?? []).find(
    (quotation) =>
      ANSWERING_SOURCES.has(quotation.source) &&
      (quotation.status === "sent" || quotation.status === "confirmed"),
  );
  return sent ? sent.number : null;
}

/**
 * The primary action of a case, in the order sales work through it. The first match
 * wins: a case still being read, one I2Q could not read, one that is not an inquiry, a
 * closed one, one already quoted, one a colleague has, the customer, the lines, a check
 * that holds the case, then the reply.
 */
export function primaryActionOf(
  detail: QuoteCaseDetail,
  lines: StripLines,
  customer: StripCustomer,
  facts: ActionFacts,
): PrimaryAction {
  if (detail.state === "queued" || detail.state === "processing") {
    // A case read before (its rows are on the page) is read again: after a customer
    // confirmation I2Q reads the prices for that account. "Reading the mail" on a page
    // full of rows with buttons read as stuck (tester round 5, 12 Oct 2026); the badge
    // and the caption say what happens and how long.
    const readBefore = wordedLines(detail.lines).length > 0 || Boolean(detail.work?.customer_by);
    if (readBefore) {
      const forCustomer = detail.work?.customer_by && customer.state === "certain";
      return {
        kind: "reading",
        label: ACTION_LABELS.readingAgain,
        caption: forCustomer ? customerConfirmedText(customer.name) : READING_AGAIN_TEXT,
        extras: [],
      };
    }
    return {
      kind: "reading",
      label: ACTION_LABELS.reading,
      caption: "I2Q is still reading this mail",
      extras: [],
    };
  }
  if (detail.engineError || detail.couldNotProcess) {
    return {
      kind: "set_status",
      label: ACTION_LABELS.setStatus,
      caption: "I2Q could not read this mail. Handle it by hand.",
      extras: ["try_again"],
    };
  }
  if (detail.state === "duplicate" || detail.duplicateOf) {
    return {
      kind: "nothing",
      label: ACTION_LABELS.nothing,
      caption: detail.duplicateOf ? `Copy of ${detail.duplicateOf}` : "Copy of another mail",
      captionCase: detail.duplicateOf ?? null,
      extras: ["reopen_as_inquiry"],
    };
  }
  if (isOutOfScopeCase(detail)) {
    const same = detail.nearDuplicateOf ?? null;
    return {
      kind: "nothing",
      label: ACTION_LABELS.nothing,
      caption: same ? `Same inquiry as ${same}` : "Not an inquiry",
      captionCase: same,
      extras: ["reopen_as_inquiry"],
    };
  }
  const open = (detail.workStatus ?? "open") === "open";
  if (!open) {
    return {
      kind: "nothing",
      label: ACTION_LABELS.nothing,
      caption: closedCaption(detail),
      extras: ["reopen"],
    };
  }
  const quoted = quotedAs(detail);
  if (quoted) {
    return {
      kind: "nothing",
      label: ACTION_LABELS.nothing,
      caption: `Quoted as ${quoted}`,
      extras: ["mark_done"],
    };
  }
  const assignee = detail.work?.assignee ?? null;
  if (assignee && !sameUser(assignee, facts.me)) {
    return {
      kind: "take_over",
      label: ACTION_LABELS.takeOver,
      caption: `${personName(assignee)} has this`,
      extras: ["next_case"],
    };
  }
  if (customer.state === "none") {
    return {
      kind: "find_customer",
      label: ACTION_LABELS.findCustomer,
      caption: "Sender not matched to an account",
      extras: ["not_for_us"],
    };
  }
  if (customer.state === "suggested") {
    const first = customer.options[0];
    if (first && confirmsInOneClick(first)) {
      return {
        kind: "confirm_customer",
        label: ACTION_LABELS.confirmCustomer(suggestedCustomerText(first, customer.options)),
        caption: "or pick another",
        account: first.account,
        extras: ["not_them"],
      };
    }
    return {
      kind: "choose_customer",
      label: ACTION_LABELS.chooseCustomer,
      caption:
        customer.options.length > 1
          ? "Two accounts fit this sender"
          : "The suggested account is a guess",
      extras: [],
    };
  }
  const stillOpen = openLines(detail.lines);
  if (lines.acceptable > 0 && stillOpen.length === 0) {
    return {
      kind: "accept",
      label: ACTION_LABELS.accept(lines.acceptable),
      caption: "All lines matched by I2Q",
      extras: ["pick_one_by_one"],
    };
  }
  if (stillOpen.length > 0) {
    // The button says the word the rows say: "Confirm n items" when every open line is a
    // suggestion to confirm, "Pick n items" as soon as one line has no item (12 Oct 2026,
    // live: 12 rows said "confirm" under a button that said "Pick 12 items").
    // A line whose item does not fit the row, or a scan line one reading gave, is checked
    // one by one (hunt round 2): never in the bulk confirm.
    const allConfirm = stillOpen.every(
      (line) =>
        lineWordOf(line) === LINE_STATUS_WORDS.confirm &&
        Boolean(line.matchedItemId) &&
        Boolean(line.lineKey) &&
        !needsOneByOne(line),
    );
    if (allConfirm) {
      // "Confirm n items" confirms them: one write, as one click on "Confirm" in each row
      // (ease round, 13 Oct 2026: the button only scrolled to the first row).
      return {
        kind: "confirm_lines",
        label: ACTION_LABELS.confirmLines(stillOpen.length),
        caption: "Confirm or pick in the draft",
        lineId: stillOpen[0]?.id,
        lineKeys: stillOpen.map((line) => line.lineKey ?? ""),
        extras: ["pick_one_by_one", "no_item", "add_line"],
      };
    }
    // The button counts the lines that need an item, as its sentence does ("1 line has no
    // item"): "Pick 69 items" stood next to it on a case with 68 suggestions and one open
    // line (review of the ease round, 13 Oct 2026). The suggestions come next, as
    // "Confirm 68 items". A suggestion without an item or a key counts as a line to pick.
    const toPick = stillOpen.filter(
      (line) => lineWordOf(line) === LINE_STATUS_WORDS.pick || !line.matchedItemId || !line.lineKey,
    );
    return {
      kind: "pick",
      label: ACTION_LABELS.pick(toPick.length || stillOpen.length),
      caption: "Confirm or pick in the draft",
      // "Pick 2 items" goes to the first row that needs an item, not to a suggestion.
      lineId: (
        stillOpen.find((line) => lineWordOf(line) === LINE_STATUS_WORDS.pick) ?? stillOpen[0]
      )?.id,
      extras: ["no_item", "add_line"],
    };
  }
  const held = heldStatusOf(detail.rules);
  if (held) {
    // A price below the limit, and the viewer may approve it: the button approves the
    // first held line (ease round, 13 Oct 2026: the approval was six clicks deep).
    const margin = heldMarginLine(detail);
    if (margin && facts.mayApprove) {
      return {
        kind: "approve_price",
        label: ACTION_LABELS.approvePriceOf(rowNumberOf(detail, margin.lineIndex)),
        caption: reasonSentence(margin.reason),
        lineId: margin.line.id,
        lineIndex: margin.lineIndex,
        ruleId: margin.reason.rule_id,
        extras: ["approve_price"],
      };
    }
    const holding = heldReasonOf(detail);
    // The button names the rule ("Check: Competitor on the sender list"), the caption
    // says what is wrong and who clears it: the rule's message and its action (tester
    // round 5, 12 Oct 2026: "Held by a rule" with the message alone left a sales person
    // without a next step).
    const reason = heldRuleWord(holding?.rule_id) ?? held.label;
    const heldLine = holding ? lineOfReason(detail, holding) : null;
    return {
      kind: "check",
      label: ACTION_LABELS.check(reason),
      caption: holding ? reasonSentence(holding) : reason,
      lineId: heldLine?.line.id,
      ruleId: holding?.rule_id,
      extras: [],
    };
  }
  if (!facts.hasDraft) {
    // Not eligible yet (no work row, a second look pending): the caption says what the
    // draft waits for; the button still names the step, nothing is ever disabled.
    return {
      kind: "prepare",
      label: ACTION_LABELS.prepare,
      caption: replyEligible(detail)
        ? "Written from the draft. Nothing is sent from here."
        : replyWaitingText(customer.state, lines.firstOpen),
      extras: ["waiting_customer"],
    };
  }
  if (facts.draftStale) {
    return {
      kind: "prepare_again",
      label: ACTION_LABELS.prepareAgain,
      caption: "Made before your changes",
      extras: ["copy_anyway", "edit_text"],
    };
  }
  if (facts.copiedAt) {
    return {
      kind: "done_and_next",
      label: ACTION_LABELS.doneAndNext,
      caption: `Copied ${formatDayTime(facts.copiedAt)}`,
      extras: ["copy_again", "waiting_customer"],
    };
  }
  return {
    kind: "copy",
    label: ACTION_LABELS.copy,
    caption: SHADOW_SENTENCE,
    extras: ["edit_text", "undo_edits", "done_and_next"],
  };
}

/** The first rule reason that holds the case, or null. */
export function heldReasonOf(detail: QuoteCaseDetail): RuleReason | null {
  return (detail.rules?.reasons ?? []).find((entry) => entry.severity === "blocking") ?? null;
}

/** A sentence trimmed and ended with one full stop; empty stays empty. */
function sentenceOf(text: string | null | undefined): string {
  const said = (text ?? "").trim().replace(/[.\s]+$/, "");
  return said ? `${said}.` : "";
}

/** What a rule says and what sales do next, as one caption. */
function reasonSentence(reason: RuleReason): string {
  return [reason.message, reason.action].map(sentenceOf).filter(Boolean).join(" ");
}

/** The rule whose hold a manager's price approval clears. */
export const PRICE_HOLD_RULE = "pricing.margin";

/** The rule that holds a line without a valid price (the blocker `price_missing`, level `must`). */
export const NO_PRICE_RULE = "pricing.no_price";

/** The line a line reason names, with its index in `detail.lines`; null for a case reason. */
function lineOfReason(
  detail: QuoteCaseDetail,
  reason: RuleReason,
): { line: QuoteLine; lineIndex: number } | null {
  if (reason.scope !== "line" || !reason.line_key) {
    return null;
  }
  const lineIndex = detail.lines.findIndex((line) => line.lineKey === reason.line_key);
  const line = detail.lines[lineIndex];
  return line ? { line, lineIndex } : null;
}

/** The first line whose price is held (a blocking `pricing.margin` reason), or null. */
export function heldMarginLine(
  detail: QuoteCaseDetail,
): { line: QuoteLine; lineIndex: number; reason: RuleReason } | null {
  for (const reason of detail.rules?.reasons ?? []) {
    if (reason.severity !== "blocking" || reason.rule_id !== PRICE_HOLD_RULE) {
      continue;
    }
    const found = lineOfReason(detail, reason);
    if (found) {
      return { ...found, reason };
    }
  }
  return null;
}

/**
 * The number of a line as the page shows it ("Line 2"): its place among the lines to
 * review (a stand-in for the whole mail is not counted). 0 for an index that is not a line.
 */
export function rowNumberOf(detail: QuoteCaseDetail, lineIndex: number): number {
  const target = detail.lines[lineIndex];
  if (!target) {
    return 0;
  }
  return detail.lines.filter((line) => !line.fallback).findIndex((line) => line === target) + 1;
}

// ─── what stands between the case and a reply ────────────────────────────────

/** The control a blocker sentence points at. */
export type BlockerTarget =
  | { kind: "customer" }
  | { kind: "line"; lineId: string }
  | { kind: "quantity" }
  | { kind: "add_line" }
  | { kind: "checks" }
  /** The documents of the Details sheet, where the photo panel shows the picture. */
  | { kind: "documents" };

/** The points that hold a reply. */
export function mustBlockers(blockers: readonly CaseBlocker[] | null | undefined): CaseBlocker[] {
  return (blockers ?? []).filter((blocker) => blocker.level === "must");
}

/** The points a reply lists as open when a person leaves them. */
export function openPoints(blockers: readonly CaseBlocker[] | null | undefined): CaseBlocker[] {
  return (blockers ?? []).filter((blocker) => blocker.level !== "must");
}

/** The index of the first line of a blocker, or null for a blocker of the case. */
function firstLineIndexOf(blocker: CaseBlocker): number | null {
  const index = blocker.line_indexes?.[0];
  return typeof index === "number" && index >= 0 ? index : null;
}

/** How many lines (or accounts) a blocker counts. */
function countOf(blocker: CaseBlocker): number {
  const count = blocker.count ?? 0;
  return count > 0 ? count : (blocker.line_indexes?.length ?? 0);
}

/**
 * The one sentence of a blocker, in the words of record (`BLOCKER_WORDS`): what is open,
 * then the control that resolves it. `rowOf` gives the number the page shows for a line
 * index; `customerName` is the name of the account a one-click confirmation offers.
 */
export function blockerSentence(
  blocker: CaseBlocker,
  ctx: { mayApprove: boolean; rowOf: (lineIndex: number) => number; customerName: string | null },
): string {
  const first = firstLineIndexOf(blocker);
  return BLOCKER_WORDS[blocker.kind]({
    n: countOf(blocker),
    k: first === null ? 0 : ctx.rowOf(first),
    name: blocker.kind === "customer_to_confirm" && blocker.one_click ? ctx.customerName : null,
    mayApprove: ctx.mayApprove,
    stale: first !== null && (blocker.stale_line_indexes ?? []).includes(first),
    ruleId: blocker.rule_id ?? null,
    // Hunt round 2 (Q-18): the instruction of the mail is the note of its open point.
    message:
      blocker.kind === "customer_instruction"
        ? (blocker.note ?? blocker.message ?? null)
        : (blocker.message ?? null),
    action: blocker.action ?? null,
    file: blocker.file ?? null,
    rowsRead: blocker.rows_read ?? null,
  });
}

/** The control a click on a blocker sentence goes to. */
export function blockerTargetOf(blocker: CaseBlocker, detail: QuoteCaseDetail): BlockerTarget {
  const first = firstLineIndexOf(blocker);
  const line = first === null ? null : (detail.lines[first] ?? null);
  switch (blocker.kind) {
    case "customer_missing":
    case "customer_to_confirm":
    case "customer_instruction":
      return { kind: "customer" };
    case "no_lines":
      return { kind: "add_line" };
    case "quantity_missing":
      return { kind: "quantity" };
    case "picture_cut":
      return { kind: "documents" };
    case "lines_to_pick":
    case "lines_to_confirm":
    case "lines_to_accept":
    case "price_held":
    case "rule_hold":
    case "quantity_to_check":
    case "price_missing":
      // A rule of the case (no line) is read in Details, under "Checks".
      return line ? { kind: "line", lineId: line.id } : { kind: "checks" };
  }
}

/** The blocker kinds each action stands for, in the order they are looked up. */
const ACTION_BLOCKERS: Partial<Record<PrimaryKind, readonly CaseBlocker["kind"][]>> = {
  find_customer: ["customer_missing"],
  confirm_customer: ["customer_to_confirm"],
  choose_customer: ["customer_to_confirm"],
  pick: ["lines_to_pick"],
  confirm_lines: ["lines_to_confirm"],
  accept: ["lines_to_accept"],
  approve_price: ["price_held"],
  check: ["price_held", "rule_hold"],
};

/**
 * The blocker the action resolves, or null when the list has none for it. "Prepare the
 * reply" stands for the first open point: the reply goes out with it listed as open.
 * "Check" on another rule than the price stands for the blocker of that rule.
 */
export function blockerForAction(
  action: PrimaryAction,
  blockers: readonly CaseBlocker[],
): CaseBlocker | null {
  if (action.kind === "prepare") {
    return openPoints(blockers)[0] ?? null;
  }
  const kinds = ACTION_BLOCKERS[action.kind];
  if (!kinds) {
    return null;
  }
  if (action.kind === "check" && action.ruleId === NO_PRICE_RULE) {
    // The rule that holds a line without a price: the server lists it once, as the point
    // "price_missing" (level `must`), not as a rule hold. Its sentence names the ways out.
    const missing =
      blockers.find(
        (blocker) => blocker.kind === "price_missing" && blocker.rule_id === NO_PRICE_RULE,
      ) ?? blockers.find((blocker) => blocker.kind === "price_missing");
    if (missing) {
      return missing;
    }
  }
  if (action.kind === "check" && action.ruleId && action.ruleId !== PRICE_HOLD_RULE) {
    const holds = blockers.filter((blocker) => blocker.kind === "rule_hold");
    return (
      holds.find((blocker) => blocker.rule_id === action.ruleId) ??
      holds[0] ??
      blockers.find((blocker) => blocker.kind === "price_held") ??
      null
    );
  }
  for (const kind of kinds) {
    const found = blockers.find((blocker) => blocker.kind === kind);
    if (found) {
      return found;
    }
  }
  return null;
}

/** The sentence next to the big button, its second sentence and where a click goes. */
export interface ActionCaption {
  text: string;
  /** What the reply says when an open point stays open; null otherwise. */
  note: string | null;
  /** The control the sentence points at; null for plain text. */
  target: BlockerTarget | null;
  /** The blocker the sentence comes from; null for a caption of the state. */
  blocker: CaseBlocker | null;
}

/**
 * The caption of the action. With `blockers` on the case, it is the sentence of the
 * blocker that belongs to the action, and a click goes to its control. Without the list
 * (I2Q still reads the case, the case is closed, an older server), or when no blocker
 * belongs to the action, it is the caption of the state, as plain text.
 */
export function captionOf(
  action: PrimaryAction,
  detail: QuoteCaseDetail,
  ctx: { mayApprove: boolean; customerName: string | null },
): ActionCaption {
  const plain: ActionCaption = { text: action.caption, note: null, target: null, blocker: null };
  if (!Array.isArray(detail.blockers)) {
    return plain;
  }
  const blocker = blockerForAction(action, detail.blockers);
  if (!blocker) {
    return plain;
  }
  return {
    text: blockerSentence(blocker, {
      mayApprove: ctx.mayApprove,
      rowOf: (lineIndex) => rowNumberOf(detail, lineIndex),
      customerName: ctx.customerName,
    }),
    note: action.kind === "prepare" ? (BLOCKER_OPEN_NOTES[blocker.kind] ?? null) : null,
    target: blockerTargetOf(blocker, detail),
    blocker,
  };
}

/** The unapplied close hint of an open case, appended to the caption; null otherwise. */
export function closeHintOf(detail: QuoteCaseDetail): string | null {
  const open = (detail.workStatus ?? "open") === "open";
  const candidate = detail.closeCandidate;
  if (!open || !candidate || candidate.applied || !candidate.sentence?.trim()) {
    return null;
  }
  return candidate.sentence.trim().replace(/[.\s]+$/, "");
}

/** The notes of a file that was not read, or not read whole (hunt round 2, Q-27, Q-28 b). */
const NOT_READ_NOTES: ReadonlySet<string> = new Set([
  "format_not_supported",
  "unreadable",
  "unreadable_text",
  "encrypted",
  "too_large",
  "too_many_pages",
  "budget",
  "model_rejected",
  "image_unclear",
  "scan",
  "heic_not_supported",
]);

/** True when an attachment of the case carries a note that it was not read. */
export function hasUnreadAttachment(detail: Pick<QuoteCaseDetail, "attachmentsRead">): boolean {
  return (detail.attachmentsRead ?? []).some(
    (entry) => !entry.inline && entry.note != null && NOT_READ_NOTES.has(entry.note),
  );
}

/** The facts the sentence of a case without lines is chosen from (Q-28 b). */
export function noLinesFactsOf(
  detail: Pick<QuoteCaseDetail, "followUpOf" | "earlierMentions" | "attachmentsRead">,
): NoLinesFacts {
  return {
    firstMail: detail.followUpOf ?? null,
    earlierMentions: (detail.earlierMentions ?? []).length,
    attachmentNotRead: hasUnreadAttachment(detail),
  };
}

/**
 * The notes of the main page over the draft (hunt round 2, Q-27): a list I2Q did not read
 * whole, and a file it did not read. Before they sat in the closed Details sheet only.
 */
export function mainPageNotesOf(
  detail: Pick<QuoteCaseDetail, "attachmentsRead">,
): { key: string; text: string }[] {
  const notes: { key: string; text: string }[] = [];
  for (const entry of detail.attachmentsRead ?? []) {
    if (entry.inline) {
      continue;
    }
    const key = `${entry.index}:${entry.child ?? ""}`;
    const file = attachmentFileText(entry);
    if (entry.note === "lines_cut") {
      notes.push({
        key: `cut:${key}`,
        text: `${file}: ${LONG_LIST_WORDS.cut(entry.lines_read ?? 0)}`,
      });
    } else if (entry.note != null && NOT_READ_NOTES.has(entry.note)) {
      const label = attachmentNoteLabel(entry.note);
      if (label) {
        notes.push({ key: `note:${key}`, text: `${file}: ${label}` });
      }
    }
  }
  return notes;
}

/**
 * True when the open case offers "Close: not an inquiry" (hunt round 2, decision 8): a
 * system notification or a newsletter. An offer only: I2Q never closes such a case.
 */
export function offersNotAnInquiryClose(
  detail: Pick<QuoteCaseDetail, "workStatus" | "closeCandidate">,
): boolean {
  const open = (detail.workStatus ?? "open") === "open";
  const candidate = detail.closeCandidate;
  return open && candidate?.kind === "not_an_inquiry" && candidate.applied !== true;
}

/**
 * How many things the "Details (n)" sheet holds: the rules that hold the case or warn,
 * and the open questions. A warning counts too (hunt round 2, Q-27: "Details (0)" while a
 * check was open).
 */
export function detailsCountOf(detail: QuoteCaseDetail, openQuestions: number): number {
  const checks = (detail.rules?.reasons ?? []).filter(
    (r) => r.severity === "blocking" || r.severity === "warning",
  ).length;
  return checks + Math.max(0, openQuestions);
}
